import { colors } from '@/assets/styles/colors';


export type SelectorColor = 'default' | 'important' | 'success' | 'alert' | 'error';

export interface SelectorOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Tooltip shown on hover when the option is disabled */
  disabledTooltip?: string;
  supportingText?: string;
  searchTokens?: string;
}

export interface SelectorProps {
  id?: string;
  label: string;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  options: SelectorOption[];
  supportingText?: string;
  placeholder?: string;
  color?: SelectorColor;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  selectClassName?: string;
  labelClassName?: string;
  supportingClassName?: string;
  multiple?: boolean;
  searchable?: boolean;
  menuClassName?: string;
  optionClassName?: string;
  portal?: boolean;
  tooltipText?: string;
  hideChevron?: boolean;
  /** Hides the label rendered above the trigger — used for compact chip-style selectors. */
  hideLabel?: boolean;
  /** Overrides the chevron icon color (defaults to the color computed from `color`/state). */
  chevronColor?: string;
  /** Overrides the chevron wrapper alignment classes.
   * Defaults to bottom-aligned, matching the standard input look. */
  chevronWrapperClassName?: string;
  'data-testid'?: string;
  
  // Pagination props for server-side data loading
  /** Callback to load the next page of options (infinite scroll) */
  onLoadMore?: () => Promise<void>;
  /** Whether there are more pages available to load */
  hasNextPage?: boolean;
  /** Whether more options are currently being loaded (pagination / infinite scroll) */
  isLoadingMore?: boolean;
  /** Whether a search/fetch operation is in progress — shows a centered spinner in the dropdown */
  isLoading?: boolean;
  /** Callback when search text changes (for server-side search) */
  onSearchChange?: (search: string) => void;
}

export const SELECTOR_COLOR_TOKENS: Record<Exclude<SelectorColor, 'default'>, string> = {
  important: colors.important.main,
  success: colors.feedback.success300,
  alert: colors.feedback.alert300,
  error: colors.feedback.error300,
};

export interface SelectorColors {
  label: string;
  supporting: string;
  body: string;
  border: string;
  icon: string;
}

export function getSelectorColors(args: {
  variant: SelectorColor;
  disabled: boolean;
  focused: boolean;
  open: boolean;
}): SelectorColors {
  const { variant, disabled, focused, open } = args;
  if (disabled) {
    return {
      label: colors.grays.neutral99,
      supporting: colors.grays.neutral99,
      body: colors.grays.neutral99,
      border: colors.grays.neutralE5,
      icon: colors.grays.neutral99,
    };
  }
  if (variant === 'default') {
    const activeBorder = (focused || open)
      ? colors.important.main
      : colors.grays.neutralE5;
    return {
      label: colors.grays.neutral66,
      supporting: colors.grays.neutral99,
      body: colors.grays.neutral33,
      border: activeBorder,
      icon: colors.important.main,
    };
  }
  const variantColor = SELECTOR_COLOR_TOKENS[variant];
  return {
    label: variantColor,
    supporting: variantColor,
    body: colors.grays.neutral33,
    border: variantColor,
    icon: variantColor,
  };
}
